import { useState, useEffect, useMemo, useRef } from 'react';
import {
  getMooringCleanedRollups,
  getMooringsByGridId,
  getMeteorologyRollups,
  getHabRiskLatest,
  getHabRisk7Day,
} from '../services/mooringService';
import { pickMooring } from '../utils/moorings';

const KINDS_P1 = [
  'pc', 'pc_fluorometer', 'cyanobacteria_index',
  'temperature', 'temperature_rdo',
  'chla', 'chla_fluorometer',
  'o2_25', 'o2_rdo', 'o2_rdo_saturation',
  'conductivity', 'turbidity_fluorometer',
];
const KINDS_P2 = [
  'wind_speed', 'wind_direction_true',
  'wind_speed_ext', 'wind_direction_ext',
  'precip_accum_1hr_ext', 'precip_accum_24hr_ext', 'air_temperature_ext',
];
const KINDS_P3 = ['wave_height', 'wave_direction', 'wave_direction_true'];

const RANGE_MS = {
  '24h': 24 * 60 * 60 * 1000,
  '3d':  3 * 24 * 60 * 60 * 1000,
  '1w':  7 * 24 * 60 * 60 * 1000,
  '2w':  14 * 24 * 60 * 60 * 1000,
  '1m':  30 * 24 * 60 * 60 * 1000,
};

export const bucketMinutesForRange = (key) => {
  const map = { '24h': 4, '3d': 16, '1w': 30, '2w': 60, '1m': 120, launch: 240 };
  return map[key] ?? 60;
};

const transformSensorData = (data) => {
  if (!Array.isArray(data) || !data.length) return {};
  const grouped = data.reduce((acc, r) => {
    if (!r?.sensor_kind || r.time === undefined || r.sensor_value === undefined) return acc;
    if (!acc[r.sensor_kind]) acc[r.sensor_kind] = [];
    acc[r.sensor_kind].push({ time: r.time, unit: r.unit, sensor_value: r.sensor_value, depth: r.depth });
    return acc;
  }, {});
  Object.keys(grouped).forEach(k => grouped[k].sort((a, b) => new Date(b.time) - new Date(a.time)));
  return grouped;
};

const pickLatestLatLon = (arr) => {
  if (!Array.isArray(arr) || !arr.length) return null;
  const hit = [...arr]
    .filter(r => Number.isFinite(r?.latitude) && Number.isFinite(r?.longitude))
    .sort((a, b) => new Date(b.time) - new Date(a.time))[0];
  return hit ? { lat: hit.latitude, lon: hit.longitude } : null;
};

const pickLatestLatLonFromPayload = (payload) => {
  if (!Array.isArray(payload)) return null;
  const waveDir = payload.filter(r => r?.sensor_kind === 'wave_direction');
  return pickLatestLatLon(waveDir) || pickLatestLatLon(payload);
};

const mergePayloads = (a, b) => {
  const safeA = Array.isArray(a) ? a : [];
  const safeB = Array.isArray(b) ? b : [];
  const seen = new Set(safeA.map(r => `${r.mooring}|${r.time}|${r.sensor_kind}|${r.depth}`));
  return [...safeA, ...safeB.filter(r => !seen.has(`${r.mooring}|${r.time}|${r.sensor_kind}|${r.depth}`))];
};

/**
 * Fetches and transforms water quality data for a pond grid.
 * @param {object} params
 * @param {string|number} params.gridId
 * @param {string} params.rangeKey  - one of '24h','3d','1w','2w','1m','launch'
 * @param {string} params.token
 * @param {string|number} [params.mooringId] - which buoy in the grid to show; falls
 *   back to the first active buoy (then the first buoy) when absent or unknown
 * @param {boolean} [params.enabled=true] - set false to skip fetching (e.g. while auth is loading)
 */
export function useWaterQualityData({ gridId, rangeKey, token, mooringId: requestedMooringId, enabled = true }) {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPriority2, setLoadingPriority2] = useState(false);
  const [loadingPriority3, setLoadingPriority3] = useState(false);
  const [error, setError] = useState(null);
  const [moorings, setMoorings] = useState([]);
  const [latestLatLon, setLatestLatLon] = useState(null);
  const [oneMonthCache, setOneMonthCache] = useState(null);
  const [habRisk, setHabRisk] = useState(null);
  const [habRisk7Day, setHabRisk7Day] = useState(null);
  const [loadingHabRisk, setLoadingHabRisk] = useState(true);
  const rangeKeyRef = useRef(rangeKey);

  useEffect(() => { rangeKeyRef.current = rangeKey; }, [rangeKey]);

  // Fetch every buoy in this grid — one response carries each buoy's latest sync,
  // unmoored date and launch date (moored_at), so no separate /installations/ call
  // is needed. Cleared on a grid change so the previous grid's buoys don't linger.
  useEffect(() => {
    setMoorings([]);
  }, [gridId]);

  useEffect(() => {
    if (!enabled || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const mRes = await getMooringsByGridId(gridId, token);
        const list = mRes?.results || mRes;
        if (cancelled) return;
        setMoorings(Array.isArray(list) ? list : []);
        // A grid with no buoys never reaches the HAB-risk fetch below, so release
        // its loading flag here — otherwise the page spins forever.
        if (!Array.isArray(list) || !list.length) setLoadingHabRisk(false);
      } catch {
        if (!cancelled) { setMoorings([]); setLoadingHabRisk(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [gridId, token, enabled]);

  // Which buoy this view is showing. Everything below is scoped to it: a grid with
  // several buoys is read one buoy at a time, chosen by the caller (from the URL).
  const selectedMooring = useMemo(
    () => pickMooring(moorings, requestedMooringId),
    [moorings, requestedMooringId]
  );
  const mooringId = selectedMooring?.id ?? null;
  const latestSync = selectedMooring?.status?.latest_sync ?? null;
  const unmooredAt = selectedMooring?.unmoored_at ?? null;
  const launchDate = selectedMooring?.moored_at ?? null;

  // Switching buoys (or grids) reuses this same hook instance, so without an
  // explicit reset the previous buoy's readings and risk linger on screen while
  // the new buoy loads. Clear everything buoy-scoped here. (oneMonthCache is left
  // alone deliberately — it carries its own buoy key, so it stays a valid cache to
  // switch back to, and clearing it here would only re-trigger the fetch below.)
  useEffect(() => {
    setLatestLatLon(null);
    setHabRisk(null);
    setHabRisk7Day(null);
    setLoadingHabRisk(true);
    setRawData([]);
    setError(null);
  }, [mooringId]);

  // launchDate only affects the memo when rangeKey is 'launch'; excluding it from
  // the deps for other ranges prevents the memo from recomputing (with a fresh
  // new Date()) every time the launch-date effect resolves.
  const effectiveLaunchDate = rangeKey === 'launch' ? launchDate : null;
  const { startDateISO, endDateISO, rangeLabel } = useMemo(() => {
    const end = new Date();
    if (rangeKey === 'launch') {
      let start = new Date('2025-01-01T00:00:00Z');
      if (effectiveLaunchDate) {
        const parsed = new Date(effectiveLaunchDate);
        if (!isNaN(parsed)) start = parsed;
      }
      // Once a mooring/sensor pack is uninstalled there's no data past that point —
      // querying through "now" just leaves a long empty tail on the chart (and asks
      // the backend to scan months of nothing). Cap the end at uninstall time.
      let launchEnd = end;
      if (unmooredAt) {
        const parsed = new Date(unmooredAt);
        if (!isNaN(parsed) && parsed < launchEnd) launchEnd = parsed;
      }
      return { startDateISO: start.toISOString(), endDateISO: launchEnd.toISOString(), rangeLabel: 'Since Launch' };
    }
    const ms = RANGE_MS[rangeKey] ?? RANGE_MS['3d'];
    return {
      startDateISO: new Date(end.getTime() - ms).toISOString(),
      endDateISO: end.toISOString(),
      rangeLabel: rangeKey,
    };
  }, [rangeKey, effectiveLaunchDate, unmooredAt]);

  // Fetch HAB risk — independent of date range, only re-runs when the mooring changes.
  useEffect(() => {
    if (!mooringId || !token) return;
    let cancelled = false;
    getHabRiskLatest(mooringId, token)
      .then(r => { if (!cancelled) { setHabRisk(r); setLoadingHabRisk(false); } })
      .catch(e => { if (!cancelled) { console.warn('[HAB Risk Latest] error:', e.message); setLoadingHabRisk(false); } });
    getHabRisk7Day(mooringId, token)
      .then(r => { if (!cancelled) setHabRisk7Day(r); })
      .catch(e => { if (!cancelled) console.warn('[HAB Risk 7-Day] error:', e.message); });
    return () => { cancelled = true; };
  }, [mooringId, token]);

  // Main data fetch: P1 first (water quality), then P2+P3 in parallel
  useEffect(() => {
    if (!mooringId || !token) return;
    let cancelled = false;

    // The 1-month payload is expensive, so it's cached — but keyed to the buoy it
    // was fetched for, otherwise switching buoys would replay the previous buoy's
    // month of data before the new fetch lands.
    if (rangeKey === '1m' && oneMonthCache?.mooringId === mooringId) {
      setRawData(oneMonthCache.data);
      setLatestLatLon(pickLatestLatLonFromPayload(oneMonthCache.data));
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setError(null);

        // For long spans, minute-level rollups time out the backend and produce
        // unnecessarily large payloads. Coarsen the bucket consistently across
        // P1/P2/P3/meteorology based on the span: 15-minute buckets once the range
        // is a week or more, and 1-hour buckets once it's a month or more — coarse
        // enough to stay fast while still preserving short-lived bloom spikes.
        const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
        const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;
        const rangeSpanMs = new Date(endDateISO) - new Date(startDateISO);
        const longRangeIntervalMin =
          rangeSpanMs >= ONE_MONTH_MS ? 60 : rangeSpanMs >= ONE_WEEK_MS ? 15 : null;
        const p1Bucket = longRangeIntervalMin ? { intervalMin: longRangeIntervalMin, agg: 'mean' } : {};
        const p1Res = await getMooringCleanedRollups(
          mooringId, { kinds: KINDS_P1, startDate: startDateISO, endDate: endDateISO, ...p1Bucket }, token
        );
        if (cancelled) return;

        const p1 = Array.isArray(p1Res) ? p1Res : [];
        setRawData(p1);
        setLatestLatLon(pickLatestLatLonFromPayload(p1));
        setLoading(false);
        setLoadingPriority2(true);
        setLoadingPriority3(true);

        // P2/P3 (and the meteorology floor below) get at least P1's long-range
        // bucket, but keep the per-range bucket when it already rolls up to a
        // coarser interval (fewer points) than P1 would — never request finer data.
        const intervalMin = Math.max(longRangeIntervalMin ?? 0, bucketMinutesForRange(rangeKey));

        // Meteorology is fetched for all gridded kinds at once. getMeteorologyRollups
        // now follows pagination, so the full window comes back regardless of page
        // size; this interval coarsening is purely to keep the payload/point count
        // reasonable over long ranges (fewer pages, lighter charts) — same idea as
        // the 1-month+ bucket above, scaled continuously to the range.
        const MET_MAX_POINTS_PER_KIND = 400;
        const metIntervalMin = Math.max(
          intervalMin,
          Math.ceil(rangeSpanMs / 60000 / MET_MAX_POINTS_PER_KIND)
        );

        // temperature_rdo_corrected powers the temperature charts and the bottom
        // O2-saturation temp input for all users. Fetch it in its own isolated
        // request (with its own .catch) so a failure on this newer kind can NEVER
        // take down the core P1/P2/P3/meteorology load — it must not be bundled
        // into the P1 kinds.
        const [p2Res, p3Res, metGridRes, tRdoCorrRes] = await Promise.all([
          getMooringCleanedRollups(mooringId, { kinds: KINDS_P2, startDate: startDateISO, endDate: endDateISO, intervalMin, agg: 'mean' }, token),
          getMooringCleanedRollups(mooringId, { kinds: KINDS_P3, startDate: startDateISO, endDate: endDateISO, intervalMin, agg: 'mean' }, token),
          getMeteorologyRollups(mooringId, {startDate: startDateISO,endDate: endDateISO,intervalMin: metIntervalMin,timeZone: 'America/New_York',}, token).catch(() => []),
          getMooringCleanedRollups(mooringId, { kinds: ['temperature_rdo_corrected'], startDate: startDateISO, endDate: endDateISO, ...p1Bucket }, token).catch(() => []),
        ]);
        if (cancelled) return;

        const p2 = Array.isArray(p2Res) ? p2Res : [];
        const p3 = Array.isArray(p3Res) ? p3Res : [];
        const metGrid = Array.isArray(metGridRes) ? metGridRes : (metGridRes?.results ?? []);
        const tRdoCorr = Array.isArray(tRdoCorrRes) ? tRdoCorrRes : [];

        setRawData(prev => {
          const merged = mergePayloads(prev, mergePayloads(p2, mergePayloads(p3, mergePayloads(metGrid, tRdoCorr))));
          setLatestLatLon(pickLatestLatLonFromPayload(merged));
          if (rangeKey === '1m') setOneMonthCache({ mooringId, data: merged });
          return merged;
        });
        setLoadingPriority2(false);
        setLoadingPriority3(false);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load data');
          setLoading(false);
          setLoadingHabRisk(false);
          setLoadingPriority2(false);
          setLoadingPriority3(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [mooringId, token, startDateISO, endDateISO, rangeKey, oneMonthCache]);

  const transformedData = useMemo(() => transformSensorData(rawData), [rawData]);

  return {
    transformedData,
    moorings,
    mooringId,
    habRisk,
    habRisk7Day,
    loading,
    loadingHabRisk,
    loadingPriority2,
    loadingPriority3,
    error,
    latestSync,
    unmooredAt,
    latestLatLon,
    launchDate,
    startDateISO,
    endDateISO,
    rangeLabel,
  };
}
